import React, {useEffect, useState} from "react";
import {isElectronApp, registerEvent} from "../electron-modules";
import {
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch, TextField
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { SingleSlider } from ".";
import clsx from 'clsx';
import { useTranslation } from "react-i18next";
import { ColorPicker, SimpleDialog } from "../components";
import {useAuth, useDialog, useGlobalSettings} from "../hooks";
import { ControlType } from "../types/controls";
import { AerialMeasureType, SamplingRate, VolumeType } from "../types/measurements";
import {
  engineCloudURL as globalEngineCloudURL,
  setEngineCloudURL as setGlobalEngineCloudURL
} from "../urls";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginTop: theme.spacing(1)
    },
    sectionTitle: {
      fontWeight: 'bold',
      display: "inline-block",
      fontSize: '0.9em'
    },
    slider: {
      width: "100%"
    },
    dropdown: {
      width: "40%"
    },
    checkBoxWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    dropdownWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }
  })
);

export const GlobalSettings = () => {
  const {signOut} = useAuth();
  const classes = useStyles();
  const {t} = useTranslation();

  const settingsDialog = useDialog();
  const globalSettings = useGlobalSettings();

  const [openQuickNav, setOpenQuickNav] = useState(false);
  const [orthoQuality, setOrthoQuality] = useState(null);
  const [orthoOpacity, setOrthoOpacity] = useState(null);
  const [controlType, setControlType] = useState(null);
  const [aerialMeasureType, setAerialMeasureType] = useState(null);
  const [volumeType, setVolumeType] = useState(null);
  const [volumeSampleRate, setVolumeSampleRate] = useState(null);
  const [viewerBackground, setViewerBackground] = useState("#00000");
  const [aerialBackground, setAerialBackground] = useState("#00000");
  const [engineCloudURL, setEngineCloudURL] = useState("");

  const onSubmit = () => {
    globalSettings.update({
      orthoQuality,
      orthoOpacity,
      controlType,
      openQuickNav,
      aerialMeasureType,
      volumeType,
      volumeSampleRate,
      viewerBackground,
      aerialBackground
    });

    if (isElectronApp) {
      const modified = setGlobalEngineCloudURL(engineCloudURL, true);

      if (modified) {
        signOut();
      }
    }

    settingsDialog.handleClose();
  };

  useEffect(() => {
    if (!settingsDialog.open) {
      return;
    }

    // Load current state from global settings context
    setOrthoQuality(globalSettings.orthoQuality);
    setOrthoOpacity(globalSettings.orthoOpacity);
    setControlType(globalSettings.controlType);
    setAerialMeasureType(globalSettings.aerialMeasureType);
    setVolumeType(globalSettings.volumeType);
    setVolumeSampleRate(globalSettings.volumeSampleRate);
    setViewerBackground(globalSettings.viewerBackground);
    setAerialBackground(globalSettings.aerialBackground);
    setOpenQuickNav(globalSettings.openQuickNav);

    if (isElectronApp) {
      setEngineCloudURL(globalEngineCloudURL);
    }
  }, [settingsDialog.open]);

  useEffect(() => {
    registerEvent("open-global-settings", () => {
      settingsDialog.handleOpen();
    });
  }, []);

  return (
    <SimpleDialog
      open={settingsDialog.open}
      onClose={settingsDialog.handleClose}
      onSubmit={onSubmit}
      title={t("global-settings.viewer-settings")}
      button={t('buttons.apply-settings')}
    >
      {/* Global Settings */}
      <List dense>

        {/** General setting header */}
        <ListItem>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t('global-settings.general_settings')}
          </Typography>
        </ListItem>

        {/* Viewer background */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.viewer_background')}
          </Typography>

          <ColorPicker
            value={viewerBackground}
            onSubmit={setViewerBackground}
          />
        </ListItem>

        {/* Aerial background */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.aerial_background')}
          </Typography>

          <ColorPicker
            value={aerialBackground}
            onSubmit={setAerialBackground}
          />
        </ListItem>

        {/* Control Type */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t("global-settings.control-type")}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={controlType}
              onChange={(event) => {
                const value = Number(event.target.value);
                setControlType(value);
              }}
            >
              <MenuItem key={0} value={ControlType.Encompass}>
                {t("control-type.default")}
              </MenuItem>
              <MenuItem key={1} value={ControlType.Reverse}>
                {t("control-type.reverse")}
              </MenuItem>
              <MenuItem key={2} value={ControlType.Autocad}>
                {t("control-type.autocad")}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Quick navigation */}
        <ListItem className={clsx(classes.header, classes.checkBoxWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.allow_quick_navigation_menu')}
          </Typography>

          <Switch
            checked={openQuickNav}
            onChange={() => setOpenQuickNav(!openQuickNav)}
            name="openQuickNav"
          />
        </ListItem>

        {/** Measurement header */}
        <ListItem className={classes.header}>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t('global-settings.measurement_settings')}
          </Typography>
        </ListItem>

        {/* Aerial Measurement Type */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.aerial_measurement_type')}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={aerialMeasureType}
              onChange={(event) => {
                const value = Number(event.target.value);
                setAerialMeasureType(value);
              }}
            >
              <MenuItem key={0} value={AerialMeasureType.Spherical}>
                {t('global-settings.aerial_spherical')}
              </MenuItem>
              <MenuItem key={1} value={AerialMeasureType.Planar}>
                {t('global-settings.aerial_planar')}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Volume Calculation */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.volume_calculation')}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={volumeType}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVolumeType(value);
              }}
            >
              <MenuItem key={0} value={VolumeType.MultiPlane}>
                {t('measure.volume_multi_plane')}
              </MenuItem>
              <MenuItem key={1} value={VolumeType.LowestPlane}>
                {t('measure.volume_lowest_plane')}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Volume Sampling */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.volume_sample_rate')}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={volumeSampleRate}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVolumeSampleRate(value);
              }}
            >
              <MenuItem key={0} value={SamplingRate.High}>
                {t('general.high')}
              </MenuItem>
              <MenuItem key={1} value={SamplingRate.Medium}>
                {t('general.medium')}
              </MenuItem>
              <MenuItem key={2} value={SamplingRate.Low}>
                {t('general.low')}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/** Orthomoaic header */}
        <ListItem className={classes.header}>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t("global-settings.orthomosaic-settings")}
          </Typography>
        </ListItem>

        {/* Orthomoaic opacity */}
        <ListItem>
          <div className={classes.slider}>
            <Typography gutterBottom variant="subtitle2">
              {t("global-settings.orthomosaic-opacity")}
            </Typography>
            <SingleSlider
              value={orthoOpacity}
              onChange={setOrthoOpacity}
              min={0}
              max={1}
              step={0.01}
              magnitude={100}
              endAdornment={"%"}
            />
          </div>
        </ListItem>

        {/* Orthomoaic quality */}
        <ListItem>
          <div className={classes.slider}>
            <Typography gutterBottom variant="subtitle2">
              {t("global-settings.orthomosaic-quality")}
            </Typography>
            <SingleSlider
              value={orthoQuality}
              onChange={setOrthoQuality}
              min={0.25}
              max={1.00}
              step={0.01}
              magnitude={100}
              endAdornment={"%"}
            />
          </div>
        </ListItem>

        {/** General setting header */}
        <ListItem>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t("global-settings.engine_cloud_settings")}
          </Typography>
        </ListItem>

        {/* Engine Cloud URL */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t("global-settings.engine_cloud_url")}
          </Typography>

          <FormControl className={classes.dropdown}>
            <TextField
              value={engineCloudURL}
              onChange={(event) => {
                setEngineCloudURL(event.target.value);
              }}
            >
            </TextField>
          </FormControl>
        </ListItem>

      </List>
    </SimpleDialog>
  );
};
